import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';

import { UserAccountService } from '../../../services/user-account/user-account.service';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule],
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.css'],
})
export class ChangePasswordComponent {
  changePasswordForm: FormGroup;
  isLoading: boolean = false;
  private static readonly SESSION_ERROR_MESSAGE_KEYS = [
    'Token Session Not Found',
    'Invalid Or Expired Token',
  ] as const;

  passwordInputs = {
    old_password: { isShown: false },
    new_password: { isShown: false },
    confirm_password: { isShown: false },
  };

  constructor(
    private fb: FormBuilder,
    private userService: UserAccountService,
    private dialogRef: MatDialogRef<ChangePasswordComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.changePasswordForm = this.fb.group({
      old_password: ['', Validators.required],
      new_password: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(16),
        ],
      ],
      confirm_password: ['', Validators.required],
    });
  }

  viewHidePassword(
    field: 'old_password' | 'new_password' | 'confirm_password',
  ): void {
    this.passwordInputs[field].isShown = !this.passwordInputs[field].isShown;
  }

  changePassword(): void {
    if (this.changePasswordForm.invalid) return;

    const { old_password, new_password, confirm_password } =
      this.changePasswordForm.value;

    if (new_password !== confirm_password) {
      alert('New password and confirm password do not match.');
      return;
    }

    this.isLoading = true;

    const payload = { old_password, new_password, confirm_password };

    this.userService.changePassword(payload).subscribe({
      next: () => {
        alert('✅ Password changed successfully!');
        this.dialogRef.close();
      },
      error: (error: any) => {
        console.error('❌ Password change failed:', error);
        if (this.isSessionExpiredError(error)) {
          // Global interceptor handles logout + navigation to /login.
          this.dialogRef.close();
          return;
        }
        alert('❌ Failed to change password. Please try again.');
        this.isLoading = false;
      },
    });
  }

  private isSessionExpiredError(error: any): boolean {
    const status = error?.status;
    const messageKey = error?.error?.message_key;
    const responseStatus = error?.error?.status;

    const isAuthStatus = status === 401 || status === 403;
    const isKnownSessionKey =
      ChangePasswordComponent.SESSION_ERROR_MESSAGE_KEYS.includes(messageKey);

    return isAuthStatus && (isKnownSessionKey || responseStatus === 'Token Expired');
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
